import { type ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getApiClient } from "../lib/apiClient";

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; merchant: { id: string; email: string } }
  | { status: "anonymous" };

/**
 * Blocks its children until the current user is resolved. In Studio
 * mode, auth.me() always returns the synthetic local merchant so this
 * is effectively a pass-through. In Connected mode, an unauthenticated
 * user is redirected to /signin.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    getApiClient()
      .auth.me()
      .then((m) => {
        if (cancelled) return;
        if (!m) {
          setState({ status: "anonymous" });
        } else {
          setState({ status: "authenticated", merchant: m });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: "anonymous" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (state.status === "anonymous") {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
